'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { ChevronDown, Star } from 'lucide-react';
import { Message, Rank } from 'iconsax-react';
import { BlueCheck, GoldCheck } from '@/components/Icons';
import { toPersianDigits } from '@/utils/persianUtils';
import PlatformSearchModal from './PlatformSearchModal';

export interface PlatformHeaderCardProps {
    postTitle?: string;
    faName?: string;
    enName?: string;
    logoUrl?: string;
    sponsored?: boolean;
    verified?: boolean;
    badge?: string;
    rank?: string | number;
    /** Real WordPress comment count (not the rating vote count) */
    commentsCount?: number;
    /** Editorial score of Khanesarmaye's experts (panel `brokerRating.finalRating` / sub-scores) */
    rating?: string | number;
    categories?: 'foreign' | 'iranian' | 'props' | 'broker' | 'gold' | undefined;
    contenttype?: string;
    currentUri?: string;
}

const PlatformHeaderCard: React.FC<PlatformHeaderCardProps> = ({
    postTitle,
    faName,
    enName,
    logoUrl,
    sponsored,
    verified,
    badge,
    rank = '-',
    commentsCount = 0,
    rating = 0,
    categories = 'broker',
    contenttype,
    currentUri,
}) => {
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    const numericRating = Math.min(5, Math.max(0, Number(rating) || 0));
    // One decimal, Persian digits and decimal sign: 2.98 → «۳», 3.07 → «۳٫۱»
    const ratingDisplay = toPersianDigits(
        String(Math.round(numericRating * 10) / 10).replace('.', '٫'),
    );

    const handleScrollToComments = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.stopPropagation();
        e.preventDefault();
        const element = document.getElementById('comments');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            window.history.pushState(null, '', '#comments');
        }
    };

    return (
        <>
            <div
                onClick={() => setIsModalOpen(true)}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setIsModalOpen(true);
                    }
                }}
                className='flex items-center gap-2 lg:min-w-72 max-lg:w-full max-lg:mb-4 dark:bg-white/5 bg-gray-100 p-2 lg:p-4 rounded-xl justify-between cursor-pointer group hover:bg-gray-200/70 dark:hover:bg-white/10 transition-colors select-none'
                title={`انتخاب و جستجوی ${faName || 'پلتفرم'}`}
            >
                <div className='flex items-center gap-2 min-w-0 flex-1'>
                    {/* Platform Logo */}
                    <div className='aspect-square size-14 shrink-0'>
                        <Image
                            src={logoUrl || ''}
                            alt={enName || faName || 'Platform Logo'}
                            className='object-contain p-0.5 rounded-xl overflow-hidden border bg-white dark:bg-white/5 border-gray-100 dark:border-white/5 w-full h-full'
                            // The first image of the page: high priority (React also preloads it),
                            // sized for its 56px box (was 200 → 256 / 640 px files).
                            loading='eager'
                            fetchPriority='high'
                            quality={50}
                            title={enName || faName || 'Platform'}
                            width={56}
                            height={56}
                        />
                    </div>

                    {/* Content Section */}
                    <header className='flex flex-col gap-1.5 lg:gap-2 items-start text-right min-w-0 flex-1'>
                        {/* SEO H1 Heading (first in DOM hierarchy, visually second via order-2) */}
                        {postTitle && (
                            <h1 className='text-xs text-primary/80 dark:text-white/80 line-clamp-2 leading-relaxed order-2 '>
                                {postTitle}
                            </h1>
                        )}

                        {/* Platform Name H2 Row (second in DOM hierarchy, visually first via order-1) */}
                        <div className='flex items-center gap-2 relative order-1 flex-wrap'>
                            <h2 className='lg:text-2xl text-lg font-bold text-gray-900 dark:text-white leading-tight'>
                                {faName}
                            </h2>
                            {enName && (
                                <span
                                    className='pt-1 text-xxs text-primary/60 dark:text-white/60 line-clamp-1'
                                    dir='ltr'
                                >
                                    {`(${enName})`}
                                </span>
                            )}
                            {sponsored ? (
                                <div className='w-4 absolute -left-5 shrink-0' title='اسپانسر شده'>
                                    <GoldCheck className='w-full h-full' />
                                </div>
                            ) : verified || badge ? (
                                <div className='w-4 absolute -left-5 shrink-0' title='تأیید شده'>
                                    <BlueCheck className='w-full h-full' />
                                </div>
                            ) : null}
                        </div>

                        {/* Stats Row (order-3) */}
                        <div className='flex flex-wrap items-center gap-x-2 gap-y-1.5 pt-1 text-xs text-primary/70 dark:text-white/80 font-yekanBakh whitespace-nowrap order-3 leading-none'>
                            {/* Rank */}
                            <div className='inline-flex items-center gap-1 shrink-0 leading-none'>
                                <Rank
                                    size='20'
                                    className='stroke-primary/50 dark:stroke-progress shrink-0'
                                />
                                <span className='text-xs leading-none inline-flex items-center gap-1'>
                                    <span className='text-primary/60 dark:text-white/70'>رتبه</span>
                                    <span className='text-primary/60 dark:text-white/80 font-bold'>
                                        {toPersianDigits(rank)}
                                    </span>
                                </span>
                            </div>

                            <span className='w-1 h-1 rounded-full bg-primary/40 dark:bg-white/40 shrink-0 self-center' />

                            {/* Comments Count */}
                            <a
                                href='#comments'
                                onClick={handleScrollToComments}
                                onKeyDown={(e) => e.stopPropagation()}
                                className='inline-flex items-center gap-1 shrink-0 leading-none group/comments cursor-pointer'
                                title='مشاهده نظرات'
                            >
                                <Message
                                    size='20'
                                    className='stroke-primary/50 dark:stroke-progress group-hover/comments:stroke-blue-500 shrink-0 transition-colors'
                                />
                                <span className='text-xs text-primary/60 dark:text-white/80 group-hover/comments:text-blue-500 dark:group-hover/comments:text-blue-400 font-yekanBakh leading-none transition-colors'>
                                    {toPersianDigits(commentsCount)} نظر
                                </span>
                            </a>

                            {/* Experts' score (editorial). Not the visitors' stars at the bottom,
                                so it gets its own label and no vote count; hidden when not set. */}
                            {numericRating > 0 && (
                                <>
                                    <span className='w-1 h-1 rounded-full bg-primary/40 dark:bg-white/40 shrink-0 self-center' />
                                    <span
                                        className='inline-flex items-center gap-1 shrink-0 leading-none'
                                        title='امتیاز کارشناسان خانه سرمایه به این پلتفرم'
                                    >
                                        <div className='relative w-4 h-4 shrink-0 flex items-center justify-center'>
                                            <div
                                                className='absolute inset-0 overflow-hidden'
                                                style={{
                                                    width: `${(numericRating / 5) * 100}%`,
                                                }}
                                            >
                                                <Star
                                                    className='fill-yellow-500 stroke-yellow-500 w-4 h-4'
                                                    strokeWidth={1.5}
                                                    size={20}
                                                />
                                            </div>
                                            <Star
                                                className='stroke-yellow-500 w-4 h-4'
                                                strokeWidth={1.5}
                                                size={20}
                                            />
                                        </div>
                                        <span className='text-xs text-primary/60 dark:text-white/80 font-yekanBakh leading-none'>
                                            امتیاز کارشناسان خانه سرمایه:{' '}
                                            <span className='font-bold'>{ratingDisplay}</span> از ۵
                                        </span>
                                    </span>
                                </>
                            )}
                        </div>
                    </header>
                </div>

                {/* Chevron Arrow on Left */}
                <div className='p-1 text-primary/40 dark:text-white/70 group-hover:text-blue-500 transition-colors shrink-0'>
                    <ChevronDown
                        className={`size-5 transition-transform duration-300 ${
                            isModalOpen ? 'rotate-180 text-blue-500' : ''
                        }`}
                    />
                </div>
            </div>

            <PlatformSearchModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                categories={categories}
                contenttype={contenttype}
                currentUri={currentUri}
                currentName={faName}
            />
        </>
    );
};

export default PlatformHeaderCard;
